import { message } from "ant-design-vue";
import { effectParams } from "./textEffectMap";
import { clamp, rotateRect, getImageAspectRatio, getVideoAspectRatio } from './index';
import { shapeParams } from "./shapes";

const allTranslationLookups = {};

export function getTranslationLookup(lang) {
  if (allTranslationLookups[lang]) {
    return allTranslationLookups[lang];
  }
  const ret = {};
  allTranslationLookups[lang] = ret;
  return ret;
}

const allAudioLookups = {};

export function getAudioLookup(lang) {
  if (allAudioLookups[lang]) {
    return allAudioLookups[lang];
  }
  const ret = {};
  allAudioLookups[lang] = ret;
  return ret;
}

export const timeFormat = (duration, precision = 2) => {
  const minute = Math.floor(duration / 60);
  const second = duration % 60;
  const secondString = second.toFixed(precision);
  return minute + ':' + (+secondString < 10 ? ('0' + secondString) : secondString);
};

export function fitCanvas(efc, screenWidth, screenHeight) {
  const rotated = rotateRect(efc);
  const rotatedTop = Math.min.apply(Math, rotated.map(p => p.y));
  const rotatedBottom = Math.max.apply(Math, rotated.map(p => p.y));
  const rotatedLeft = Math.min.apply(Math, rotated.map(p => p.x));
  const rotatedRight = Math.max.apply(Math, rotated.map(p => p.x));
  const compareAspectRatio = (rotatedRight - rotatedLeft) / (rotatedBottom - rotatedTop) > screenWidth / screenHeight;

  const scale = compareAspectRatio ? screenWidth / (rotatedRight - rotatedLeft) : screenHeight / (rotatedBottom - rotatedTop);

  const scaleHeight = efc.height * scale;
  const scaleWidth = efc.width * scale;
  return {
    height: Math.round(scaleHeight),
    width: Math.round(scaleWidth),
    top: Math.round((screenHeight - scaleHeight) / 2),
    left: Math.round((screenWidth - scaleWidth) / 2)
  };
}

const alignMap2 = {
  center: 'center',
  West: 'left',
  East: 'right'
};

const reverseVerticalAlignMap = {
  North: 'top',
  South: 'bottom'
};

export const timeParser = (millisecond) => {
  const minute = Math.floor(millisecond / 1000 / 60);
  const second = millisecond / 1000 % 60;
  return minute + ':' + (second < 10 ? ('0' + second) : second)
};

export const fakeFunction = () => {
  message.info('该功能正在紧张开发中')
};

export const checkFileSize = (file) => {
  if (file.type.includes('video') && file.size > 30 * 1024 * 1024) {
    message.error(`视频文件超过了30M限制`);
    return false;
  }

  if (file.type.includes('image') && file.size > 10 * 1024 * 1024) {
    message.error(`图片文件超过了10M限制`);
    return false;
  }
  return true;
};


const materialEffect = {
  id: '',
  type: '',
  locked: false,
  hidden: false,
  content: {
    src: '',
    name: 'Template Material',
    top: 0,
    left: 0,
    width: 100,
    height: 100,
    rotate: 0,
    brightness: 1,
    opacity: 100,
    crop: null,
    loop: false,
    repeat: false,
    playbackRate: 1,
    volume: 100
  },
  tracks: [],
  enter: 'none',
  enterDuration: 1e3,
  leave: 'none',
  leaveDuration: 1e3,
  showTracks: false,
  start: 0,
  duration: 1000
};

const newFragment = {
  id: '',
  name: '',
  start: 0,
  duration: 6e3,
  tracks: [],
  effects: [],
  background: null,
  transition: null,
  backgroundColor: '#fff'
};

const newTextItem = {
  id: '',
  type: 'text',
  locked: false,
  hidden: false,
  content: {
    text: 'Text',
    name: '',
    top: 0,
    left: 0,
    width: 270,
    height: 45,
    rotate: 0,
    brightness: 1,
    opacity: 100,
    scale: 1,
    fontSize: 48,
    color: '#333',
    shadow: false,
    shadowX: 1,
    shadowY: 3,
    shadowBlur: 5,
    shadowColor: '#000000',
    align: 'center',
    lineHeight: 1.4,
    fontFamily: 'Montserrat-Regular',
    autoFontSize: true,
    letterSpacing: 0
  },
  tracks: [],
  enter: 'none',
  enterDuration: 1e3,
  leave: 'none',
  leaveDuration: 1e3,
  showTracks: false
};

const tracksTransformer = (effect = {}, delay, id = -1) => {
  if (Array.isArray(effect) && effect.length) {
    return [].concat.apply([], effect.map((ef, i) => tracksTransformer(ef, delay, i)));
  }

  const { title, begin, duration, forceRule, ...params } = effect;
  const effectType = title || forceRule && forceRule.type;
  return [effectType
    ? {
      id,
      start: delay + begin,
      duration,
      text: "animation",
      effectType,
      params: effectParams[effectType]
        ? {
          ...effectParams[effectType],
          ...params
        }
        : null
    }
    : {
      id,
      start: 0,
      duration: 1e3,
      text: "animation",
      effectType: 'gradientFade',
      params: {
        direction: 'left'
      }
    }
  ];
};

export const effectsTransformer = async (rule = {}, aspectRatio, screenRatio, screenHeight, resolution, seed) => {
  const now = +new Date() + (seed || '');
  const { imgrule = [], titleRule = [], otherRule = [] } = rule;
  const templateHeight = resolution.replace(/[^*]+\*/, '');
  const templateRatio = +(screenHeight / templateHeight);
  // TODO: read image aspect ratios.
  const imageEffect = await Promise.all(imgrule.map(async (image, index) => {

    const url = image.img.file || '';
    const isImage = +image.img.type === 1;
    const isSticker = +image.img.type === 3;
    const full = +image.full === 1;
    let width = (full ? screenRatio * templateHeight : +(image.size && image.size[0]) || 100) * templateRatio / aspectRatio;
    let height = 0;
    let left = 0;
    let top = 0;

    const position = image.origin_position || image.position;

    const effect = image.effect || {};

    const fullSlide = full && effect.title === 'slide';

    const shadow_offset = [];

    if (typeof image.shadow_offset === 'string') {
      image.shadow_offset.replace(/\(([0-9.]+),([0-9.]+)\)/, (_, first, second) => {
        shadow_offset.push(+first, +second);
      });
    } else if (image.shadow_offset) {
      shadow_offset.push.apply(shadow_offset, image.shadow_offset);
    } else {
      shadow_offset.push(0, 0);
    }

    // 如果动画类型为滑入，全屏展示，增长素材选中框的宽度
    if (fullSlide) {
      width += effect.distance / aspectRatio;
    }

    let matAspectRatio;

    // FIXME: retrieve the real aspect ratios
    if (image.img.resolution) {
      matAspectRatio = image.img.resolution[0] / image.img.resolution[1];
    } else if (isImage) {
      matAspectRatio = await getImageAspectRatio(url);
    } else if (isSticker) {
      matAspectRatio = image.size ? image.size[0] / image.size[1] : 1
    } else {
      matAspectRatio = await getVideoAspectRatio(url);
    }

    height = width / matAspectRatio;

    if (fullSlide) {
      let availableHeight = screenHeight / aspectRatio;
      if (height < availableHeight) {
        width *= availableHeight / height;
        height = availableHeight;
      } else {
        top = (availableHeight - height) / 2;
      }
    } else if (position) {
      left = position[0] * templateRatio / aspectRatio;
      top = position[1] * templateRatio / aspectRatio;
    }

    if (isSticker) {
      console.log('sticker detected');
      return {
        ...materialEffect,
        type: 'sticker',
        id: [now, 'sticker', index].join(''),
        content: {
          ...materialEffect.content,
          top,
          left,
          stickerHTML: url,
          width,
          height,
          opacity: image.alpha >= 0 ? image.alpha : 100,
          rotate: image.rotate ? 360 - image.rotate : 0,
          shadow: !!image.shadow_color,
          shadowX: shadow_offset[0],
          shadowY: shadow_offset[1],
          shadowBlur: image.shadow,
          shadowColor: image.shadow_color,
        },
        start: image.delay,
        duration: image.duration,
        showTracks: !!(effect.title || effect.length),
        tracks: tracksTransformer(image.effect, image.delay),
        locked: !!image.locked
      };
    }

    const type = isImage ? 'image' : 'video';
    return {
      ...materialEffect,
      type,
      id: [now, type, index].join(''),
      name: url,
      content: {
        ...materialEffect.content,
        top,
        left,
        src: url,
        width,
        height,
        opacity: image.alpha >= 0 ? image.alpha : 100,
        rotate: image.rotate ? 360 - image.rotate : 0,
        shadow: !!image.shadow_color,
        shadowX: shadow_offset[0],
        shadowY: shadow_offset[1],
        shadowBlur: image.shadow,
        shadowColor: image.shadow_color,
      },
      start: image.delay,
      duration: image.duration,
      showTracks: !!(effect.title || effect.length),
      tracks: tracksTransformer(image.effect, image.delay),
      locked: !!image.locked,
      videoSeriesTags: image.logo ? [1, '109'] : null
    }
  }).concat(otherRule.map((cur, i) => ({
    id: [now, 'shape', i].join(''),
    type: 'shape',
    locked: false,
    hidden: false,
    content: {
      shape: 'rect',
      name: '',
      top: 0,
      left: 0,
      width: cur.size[0] / aspectRatio,
      height: cur.size[1] / aspectRatio,
      color: cur.src,
      stroke: '#333',
      strokeWidth: 0,
      rotate: 0,
      opacity: Math.round(cur.alpha / 2.55)
    },
    tracks: [
      {
        id: 1,
        start: 1e3,
        duration: 1e3,
        text: 'animation',
        effectType: 'gradientFade',
        params: {
          direction: 'left'
        }
      }
    ],
    enter: 'none',
    enterDuration: 1e3,
    leave: 'none',
    leaveDuration: 1e3,
    showTracks: false,
    start: 0,
    duration: cur.duration
  }))));

  const titleEffect = titleRule.map((item, index) => {
    const { title, font, color, align = '', size, fontsize, shadow_color, shadow,
      effect = {}, fadein, fadeout
    } = item;

    const shadow_offset = [];
    const position = item.origin_position || item.position || [0, 0];

    if (typeof item.shadow_offset === 'string') {
      item.shadow_offset.replace(/\(([0-9.]+),([0-9.]+)\)/, (_, first, second) => {
        shadow_offset.push(+first, +second);
      });
    } else if (item.shadow_offset) {
      shadow_offset.push.apply(shadow_offset, item.shadow_offset);
    } else {
      shadow_offset.push(0, 0);
    }

    let horizontalAlign = 'center', verticalAlign = 'middle';
    align.split(/(?=East|center|West)/).forEach(a => {
      if (alignMap2[a]) {
        horizontalAlign = alignMap2[a];
      }
      if (reverseVerticalAlignMap[a]) {
        verticalAlign = reverseVerticalAlignMap[a];
      }
    });
    return {
      ...newTextItem,
      id: [now, 'text', index].join(''),
      content: {
        ...newTextItem.content,
        text: title.join('\n'),
        left: position[0] * templateRatio / aspectRatio,
        top: position[1] * templateRatio / aspectRatio,
        fontFamily: font,
        color,
        align: horizontalAlign,
        verticalAlign,
        width: size[0] * templateRatio / aspectRatio,
        height: size[1] * templateRatio / aspectRatio,
        // autoFontSize: false,
        fontSize: fontsize * templateRatio,
        shadow: !!shadow_color,
        shadowX: shadow_offset[0],
        shadowY: shadow_offset[1],
        shadowBlur: shadow,
        shadowColor: shadow_color,
        lineHeight: (typeof item.interline !== 'undefined' ? item.interline : 10) / fontsize + 1,
        opacity: item.alpha >= 0 ? item.alpha : 100,
        rotate: item.rotate ? 360 - item.rotate : 0,
        letterSpacing: 0
      },
      showTracks: !!(effect.title || effect.length),
      tracks: tracksTransformer(effect, item.delay),
      start: item.delay,
      duration: item.duration,
      enter: fadein ? 'enter' : 'none',
      enterDuration: fadein,
      leave: fadeout ? 'enter' : 'none',
      leaveDuration: fadeout,
      locked: !!item.locked
    }
  });

  // 合并返回图片/视频素材
  return {
    values: {
      ...imageEffect.reduce((pre, cur) => {
        return { ...pre, [cur.id]: cur }
      }, {}),
      ...titleEffect.reduce((pre, cur) => {
        return { ...pre, [cur.id]: cur }
      }, {})
    },
    keys: [...imageEffect, ...titleEffect].map(x => x.id)
  }
};

export const fragmentsTransformer = (rule = {}, start = 0, fragmentDuration) => {
  return { ...newFragment, start, duration: fragmentDuration * 1000, id: Date.now() + '', audios: [] };
};

// export const volumeSectionParser = (arr) => {
//   let temp = arr.shift();
//   let ret = [];
//   let j = 0;
//   for (let i = 0, len = arr.length; i < len; i++) {
//     let obj = arr[i];
//     if (obj[0] > temp[0]) {
//       if (obj[0] > temp[1]) {
//         break;
//       } else {
//         temp[1] = Math.max(temp[1], obj[1]);
//         j++;
//       }
//     } else if (obj[0] === temp[0]) {
//       temp[1] = Math.max(temp[1], obj[1]);
//       j++
//     }
//   }
//   arr.splice(0, j);
//   ret.push(temp);
//   return arr.length ? ret.concat(volumeSectionParser(arr)) : ret;
// }

export const other_ruleParser = (other_rule) => {
  let timeArr = [];
  other_rule.forEach(({ begin, duration, imgrule, audios = [] }) => {
    imgrule.forEach(obj => {
      if (obj.type === 'video') {
        const efc = obj.content;
        if (obj.start < duration) { // only intersecting videos are respected
          timeArr.push(
            {
              time: obj.start + begin,
              volume: efc.musicVolume,
              id: obj.id,
              type: 'enter'
            },
            {
              time: Math.min(obj.start + obj.duration + begin, duration + begin),
              volume: efc.musicVolume,
              id: obj.id,
              type: 'exit'
            }
          );
        }
      }
    });
    audios.forEach(audio => {
      const { musicVolume = 100 } = audio;
      timeArr.push(
        {
          time: audio.start + begin,
          volume: musicVolume,
          id: audio.id,
          type: 'enter'
        },
        {
          time: audio.start + audio.duration + begin,
          volume: musicVolume,
          id: audio.id,
          type: 'exit'
        }
      );
    });
  });
  return timeArr.sort((a, b) => a.time - b.time);
};

export function getFadeFactor(musicFade, time, duration) {
  let fadeFactor = 1;
  if (musicFade) {
    const { delay = 0 } = musicFade;
    if (musicFade.fadeIn) {
      fadeFactor = clamp((time - delay) / musicFade.fadeInDuration, 0, 1);
    }
    if (musicFade.fadeOut) {
      fadeFactor = Math.min(
        fadeFactor,
        clamp((duration - time + delay) / musicFade.fadeOutDuration, 0, 1)
      );
    }
  }
  return fadeFactor;
}

export function currentVolume(timeVolumes, time) {
  const obj = {};
  timeVolumes.some(item => {
    if (item.time > time) {
      return true;
    }

    if (item.type === 'enter') {
      obj[item.id] = item;
    } else {
      delete obj[item.id];
    }

    return false;
  });

  return Math.min.apply(Math, [100].concat(Object.keys(obj).map(key => obj[key].volume)));
}

export function matchFilter(effect, filter, shapeNames = {}, mode = false) {
  if (!effect || !effect.content) {
    // console.log('no effect to match');
    return true;
  }
  if (mode) {
    return !!effect.videoSeriesTags;
  }
  switch (filter) {
    case 'text':
    case 'image':
    case 'video':
    case 'shape':
    case 'lottie':
    case 'sticker':
    case 'container':
    case 'textContainer':
    case 'group':
    case 'wordArt': {
      return effect.type === filter;
    }

    case 'locked':
      return effect.locked;
    case 'unlocked':
      return !effect.locked;
    case 'hidden':
      return effect.hidden;
    case 'visible':
      return !effect.hidden;
  }

  if (/^n:/.test(filter)) {
    const nameFilter = new RegExp(filter.slice(2).replace(/([.\\()[\]^$?*+{}])/g, '\\$1'), 'i');
    switch (effect.type) {
      case 'text':
      case 'textContainer':
      case 'wordArt':
        return nameFilter.test(effect.content.name) || nameFilter.test(effect.content.text);
      case 'shape':
      case 'container':
        return nameFilter.test(effect.content.name) || nameFilter.test(effect.content.shape) || nameFilter.test(shapeNames[effect.content.shape]);
      case 'image':
      case 'video':
      case 'lottie':
      case 'group':
        return nameFilter.test(effect.content.name);
    }
    return false;
  }

  return true;
}

const normalizeCatId = id => {
  switch (id) {
    case '110':
      return '106';
    case '206':
      return '205';
  }
  return id;
};

export function normalizeEffects(effects, defaultMusicVolume = 100) {
  Object.keys(effects).forEach(k => {
    const effect = effects[k];
    const efc = effect.content;
    if (effect.type === 'text') {
      efc.text = efc.text || '';
      if (typeof efc.autoFontSize === 'undefined') {
        efc.autoFontSize = true;
      }
      if (typeof efc.verticalAlign === 'undefined') {
        efc.verticalAlign = 'middle';
      }
      if (typeof efc.name === 'undefined') {
        efc.name = '';
      }
      if (typeof efc.letterSpacing === 'undefined') {
        efc.letterSpacing = 0;
      }
    } else if (effect.type === 'video') {
      const videoProps = {
        mute: false,
        volume: 100,
        musicVolume: defaultMusicVolume,
        fadeIn: false,
        fadeInDuration: 1e3,
        fadeOut: false,
        fadeOutDuration: 1e3
      };
      Object.keys(videoProps).forEach(vp => {
        if (typeof efc[vp] === 'undefined') {
          efc[vp] = videoProps[vp];
        }
      });
      if (!efc.playbackRate) {
        efc.playbackRate = 1;
      }
    }
    if (effect.type === 'shape' || effect.type === 'container' || effect.type === 'textContainer') {
      if (typeof efc.name === 'undefined') {
        efc.name = '';
      }
    }
    if (effect.type === 'shape') {
      if (typeof efc.strokeNumeric === 'undefined') {
        efc.strokeNumeric = false;
      }
    }
    if (effect.type === 'shape' || effect.type === 'container') {
      const params = shapeParams[efc.shape];
      Object.keys(params).forEach(k => {
        if (typeof efc[k] === 'undefined') {
          efc[k] = params[k];
        }
      });
    }
    if (effect.type === 'image' || effect.type === 'video') {
      if (typeof efc.radius === 'undefined') {
        efc.radius = '';
      }

      if (typeof efc.src === 'string') {
        efc.src = efc.src.replace(
          /^(https?:)?\/\/(?:aivideo8\.oss-accelerate\.aliyuncs\.com|aivideo8\.oss-cn-hongkong\.aliyuncs\.com)/,
          'https://img001.aivideo8.com'
        );
      }
    }
    if (effect.tracks) {
      effect.tracks.forEach(tr => {
        if (effectParams[tr.effectType]) {
          tr.params = {
            ...effectParams[tr.effectType],
            ...(tr.params || {})
          };
        } else {
          tr.params = tr.params || null;
        }
      });
    }
    if (effect.videoSeriesTags) {
      effect.videoSeriesTags = effect.videoSeriesTags.map(normalizeCatId);
    }
  });
}

export function normalizeAudios(audios) {
  const defaultValues = {
    musicVolume: 100,
    playbackRate: 1,
    fadeIn: false,
    fadeInDuration: 1e3,
    fadeOut: false,
    fadeOutDuration: 1e3
  };
  Object.keys(audios).forEach(k => {
    const audio = audios[k];
    Object.keys(defaultValues).forEach(k => {
      if (typeof audio[k] === 'undefined') {
        audio[k] = defaultValues[k];
      }
    });
  });
}

export const marginFactors = {
  1920: [95, 380],
  1280: [63, 252],
  1080: [55, 220],
  960: [48, 192],
  720: [36, 144],
  540: [25, 100]
};

export function getCropDuration(efc) {
  if (efc && efc.crop && !efc.loop) {
    const [start, end] = efc.crop;
    return (end - start) * 1e3 / clamp(+efc.playbackRate || 1, .1, 10);
  }
  return Infinity;
}

export function getEffectName(effect, shapeNames) {
  let effectName = effect.content.name;
  switch (effect.type) {
    case 'text':
    case 'textContainer':
    case 'wordArt':
      effectName = effectName || effect.content.text;
      break;
    case 'shape':
    case 'container':
      effectName = effectName || shapeNames[effect.content.shape];
      break;
    case 'group':
      effectName = `${effectName} (${effect.children.length})`;
      break;
  }

  return effectName;
}

export const labelPrefixes = {
  text: '文本',
  video: '视频',
  image: '图片',
  shape: '形状',
  container: '容器',
  sticker: '贴纸',
  lottie: '动画',
  textContainer: '文本容器',
  group: '组合',
  wordArt: '艺术字',
  icon: '图标'
};

export function normalizeSVGSource(source) {
  const parser = new DOMParser();
  const dom = parser.parseFromString(source, 'application/xml');
  const root = dom.documentElement;
  let parsedSource = source;
  if (!root.getAttribute('width')) {
    const vb = root.getAttribute('viewBox');
    if (vb) {
      const [x0, y0, x1, y1] = vb.split(/\s+/);
      root.setAttribute('width', x1 - x0);
      root.setAttribute('height', y1 - y0);
      parsedSource = new XMLSerializer().serializeToString(root);
    }
  }

  const w = parseFloat(root.getAttribute('width'));
  const h = parseFloat(root.getAttribute('height'));

  return {
    source: parsedSource,
    aspectRatio: w / h || 1
  };
}

export function normalizeFragments(frags) {
  let start = 0;
  frags.forEach(frag => {
    frag.start = start;
    start += frag.duration;
    if (frag.transition) {
      start -= frag.transition.duration;
    }
  });
}

export function formatSize(value) {
  let displayValue = value, unit = 'KB';
  if (displayValue >= 1024) {
    displayValue /= 1024;
    unit = 'MB';
  }
  if (displayValue >= 1024) {
    displayValue /= 1024;
    unit = 'GB';
  }
  return (displayValue % 1 ? displayValue.toFixed(2) : displayValue) + unit;
}

export const copyValue = (val) => {
  if (navigator.clipboard) {
    return  navigator.clipboard.writeText(val)
  } else {
    const _input = document.createElement("input"); // 直接构建input
    _input.value = val; // 设置内容
    document.body.appendChild(_input); // 添加临时实例
    _input.select(); // 选择实例内容
    return new Promise((resolve,reject) => {
      document.execCommand("Copy")?resolve():reject(); // 执行复制
      document.body.removeChild(_input);
    })

  }

}